import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Asset } from 'contentful';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderingContent = (params: Params = {}) => {
  const { pause } = params;

  const { formatMessage } = useIntl();

  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { data, fetching } = useContentfulContentTypeEntry<Content>({
    contentType: GROUP_ORDERING_CONTENT_ENTRY_ID,
    pause: !isGroupOrderingEnabled ? true : pause,
  });

  const fields = data?.fields;

  // ─── Derived Data ────────────────────────────────────────────────────

  const nameFieldPlaceholder = useMemo(() => {
    const placeholderOptions =
      fields?.createGroupOrderPlaceholderNames?.filter((placeholder) => placeholder.trim()) ?? [];

    if (!placeholderOptions.length) {
      return formatMessage(messages.nameFieldPlaceholder);
    }

    const randomIndex = Math.floor(Math.random() * placeholderOptions.length);

    return placeholderOptions[randomIndex] ?? formatMessage(messages.nameFieldPlaceholder);
  }, [fields?.createGroupOrderPlaceholderNames, formatMessage]);

  const fallbackIntroCards = useMemo<IntroCard[]>(() => {
    const badge = formatMessage(messages.introSlideBadge);

    return [
      {
        fields: {
          badge,
          title: formatMessage(messages.introShareSlideTitle),
          text: formatMessage(messages.introShareSlideText),
        },
      },
      {
        fields: {
          badge,
          title: formatMessage(messages.introInviteSlideTitle),
          text: formatMessage(messages.introInviteSlideText),
        },
      },
      {
        fields: {
          badge,
          title: formatMessage(messages.introCheckoutSlideTitle),
          text: formatMessage(messages.introCheckoutSlideText),
        },
      },
    ];
  }, [formatMessage]);

  const mappedContent = useMemo<MappedContent>(() => {
    const {
      entryPointCtaLabel,
      introModalNextButtonLabel,
      introModalCancelButtonLabel,
      introModalStartOrderButtonLabel,
      lockCartModalTitle,
      lockCartModalBody,
      lockCartModalDismissButtonLabel,
      lockCartModalConfirmButtonLabel,
      lockedCartStatus,
      unlockCartButtonLabel,
      lockedCartCheckoutButtonLabel,
      unlockCartErrorMessage,
      groupOrderingNotEnabledErrorMessage,
      reorderCartModalTitle,
      reorderCartModalBody,
      reorderCartModalDismissButtonLabel,
      reorderCartModalConfirmButtonLabel,
      introModalSlides,
    } = fields ?? {};

    return {
      entryPointCtaLabel: entryPointCtaLabel ?? formatMessage(messages.entryPointCtaLabel),

      introModalNextButtonLabel:
        introModalNextButtonLabel ?? formatMessage(messages.nextButtonLabel),
      introModalCancelButtonLabel:
        introModalCancelButtonLabel ?? formatMessage(messages.cancelButtonLabel),
      introModalStartOrderButtonLabel:
        introModalStartOrderButtonLabel ?? formatMessage(messages.startOrderButtonLabel),

      lockCartModalTitle: lockCartModalTitle ?? formatMessage(messages.lockCartModalTitle),
      lockCartModalBody: lockCartModalBody ?? formatMessage(messages.lockCartModalBody),
      lockCartModalDismissButtonLabel:
        lockCartModalDismissButtonLabel ?? formatMessage(messages.lockCartModalDismissButtonLabel),
      lockCartModalConfirmButtonLabel:
        lockCartModalConfirmButtonLabel ?? formatMessage(messages.lockCartModalConfirmButtonLabel),
      lockedCartStatus: lockedCartStatus ?? formatMessage(messages.lockedCartStatus),
      unlockCartButtonLabel: unlockCartButtonLabel ?? formatMessage(messages.unlockCartButtonLabel),
      lockedCartCheckoutButtonLabel:
        lockedCartCheckoutButtonLabel ?? formatMessage(messages.lockedCartCheckoutButtonLabel),
      unlockCartErrorMessage:
        unlockCartErrorMessage ?? formatMessage(messages.unlockCartErrorMessage),
      groupOrderingNotEnabledErrorMessage:
        groupOrderingNotEnabledErrorMessage ?? formatMessage(messages.groupOrderingNotEnabledErrorMessage), // prettier-ignore

      reorderCartModalTitle: reorderCartModalTitle ?? formatMessage(messages.reorderCartModalTitle),
      reorderCartModalBody: reorderCartModalBody ?? formatMessage(messages.reorderCartModalBody),
      reorderCartModalDismissButtonLabel:
        reorderCartModalDismissButtonLabel ?? formatMessage(messages.reorderCartModalDismissButtonLabel), // prettier-ignore
      reorderCartModalConfirmButtonLabel:
        reorderCartModalConfirmButtonLabel ?? formatMessage(messages.reorderCartModalConfirmButtonLabel), // prettier-ignore

      nameFieldPlaceholder,
      introCards: introModalSlides?.length ? introModalSlides : fallbackIntroCards,
    };
  }, [fallbackIntroCards, fields, formatMessage, nameFieldPlaceholder]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetching: fetching,
    mappedContent,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  entryPointCtaLabel: {
    defaultMessage: 'Start group order',
    description: 'Home | Group ordering | Fallback entry point CTA label',
  },
  nextButtonLabel: {
    defaultMessage: 'Next',
    description: 'Home | Group ordering intro | Fallback next button label',
  },
  cancelButtonLabel: {
    defaultMessage: 'Cancel',
    description: 'Home | Group ordering intro | Fallback cancel button label',
  },
  startOrderButtonLabel: {
    defaultMessage: 'Start group order',
    description: 'Home | Group ordering intro | Fallback start order button label',
  },
  introSlideBadge: {
    defaultMessage: 'New!',
    description: 'Home | Group ordering intro | Fallback slide badge',
  },
  introShareSlideTitle: {
    defaultMessage: 'SG group ordering',
    description: 'Home | Group ordering intro | Fallback share slide title',
  },
  introShareSlideText: {
    defaultMessage:
      'Share your custom link with up to 20 guests to join your group order. You can cover the bill or choose to split it.',
    description: 'Home | Group ordering intro | Fallback share slide text',
  },
  introInviteSlideTitle: {
    defaultMessage: 'Invite to add items',
    description: 'Home | Group ordering intro | Fallback invite slide title',
  },
  introInviteSlideText: {
    defaultMessage:
      'Once someone joins, they can add and submit their items directly from their devices— no more asking, “What do you want?”',
    description: 'Home | Group ordering intro | Fallback invite slide text',
  },
  introCheckoutSlideTitle: {
    defaultMessage: 'Seamless ordering',
    description: 'Home | Group ordering intro | Fallback checkout slide title',
  },
  introCheckoutSlideText: {
    defaultMessage:
      'When it’s time to check out, you’ll submit the order for everyone. After that, you and your guests can track it until it arrives—then enjoy!',
    description: 'Home | Group ordering intro | Fallback checkout slide text',
  },
  lockCartModalTitle: {
    defaultMessage: 'Ready to check out?',
    description: 'Bag | Group ordering | Lock cart modal | Title',
  },
  lockCartModalBody: {
    defaultMessage:
      'Once you check out, the cart locks so others can’t add or edit items. You can go back anytime before you pay.',
    description: 'Bag | Group ordering | Lock cart modal | Body',
  },
  lockCartModalDismissButtonLabel: {
    defaultMessage: 'Not yet',
    description: 'Bag | Group ordering | Lock cart modal | Dismiss CTA label',
  },
  lockCartModalConfirmButtonLabel: {
    defaultMessage: 'Lock cart & check out',
    description: 'Bag | Group ordering | Lock cart modal | Confirm CTA label',
  },
  lockedCartStatus: {
    defaultMessage: "Cart locked — others can't edit while you check out.",
    description: 'Bag | Group ordering | Locked cart status',
  },
  unlockCartButtonLabel: {
    defaultMessage: 'Unlock cart',
    description: 'Bag | Group ordering | Unlock cart CTA label',
  },
  lockedCartCheckoutButtonLabel: {
    defaultMessage: 'Confirm and order',
    description: 'Bag | Group ordering | Locked cart checkout CTA label',
  },
  unlockCartErrorMessage: {
    defaultMessage: 'We couldn’t unlock the group order. Please try again.',
    description: 'Bag | Group ordering | Unlock cart error',
  },
  groupOrderingNotEnabledErrorMessage: {
    defaultMessage: 'Sorry, group ordering isn’t enabled at this location.',
    description: 'Group ordering | Group ordering not enabled error message',
  },
  reorderCartModalTitle: {
    defaultMessage: 'Ready to update your order?',
    description: 'Group ordering | Reorder cart modal | Title',
  },
  reorderCartModalBody: {
    defaultMessage:
      'Updating your order temporarily locks the cart. Others can add or edit items again when you’re done.',
    description: 'Group ordering | Reorder cart modal | Body',
  },
  reorderCartModalDismissButtonLabel: {
    defaultMessage: 'Not yet',
    description: 'Group ordering | Reorder cart modal | Dismiss CTA label',
  },
  reorderCartModalConfirmButtonLabel: {
    defaultMessage: 'Continue',
    description: 'Group ordering | Reorder cart modal | Confirm CTA label',
  },
  nameFieldPlaceholder: {
    defaultMessage: 'Cal E.',
    description: 'Group ordering | Name field fallback placeholder',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ORDERING_CONTENT_ENTRY_ID = 'orderGroupOrdering';

// ─── Types ───────────────────────────────────────────────────────────────────

export type IntroCard = {
  fields: {
    badge?: string;
    title: string;
    text: string;
    image?: Asset;
  };
};

type Content = {
  entryPointCtaLabel: string;
  introModalNextButtonLabel: string;
  introModalCancelButtonLabel: string;
  introModalStartOrderButtonLabel: string;
  lockCartModalTitle?: string;
  lockCartModalBody?: string;
  lockCartModalDismissButtonLabel?: string;
  lockCartModalConfirmButtonLabel?: string;
  lockedCartStatus?: string;
  unlockCartButtonLabel?: string;
  lockedCartCheckoutButtonLabel?: string;
  unlockCartErrorMessage?: string;
  groupOrderingNotEnabledErrorMessage?: string;
  reorderCartModalTitle?: string;
  reorderCartModalBody?: string;
  reorderCartModalDismissButtonLabel?: string;
  reorderCartModalConfirmButtonLabel?: string;
  createGroupOrderPlaceholderNames?: string[];
  introModalSlides?: IntroCard[];
};

type MappedContent = {
  entryPointCtaLabel: string;
  introModalNextButtonLabel: string;
  introModalCancelButtonLabel: string;
  introModalStartOrderButtonLabel: string;
  lockCartModalTitle: string;
  lockCartModalBody: string;
  lockCartModalDismissButtonLabel: string;
  lockCartModalConfirmButtonLabel: string;
  lockedCartStatus: string;
  unlockCartButtonLabel: string;
  lockedCartCheckoutButtonLabel: string;
  unlockCartErrorMessage: string;
  groupOrderingNotEnabledErrorMessage: string;
  reorderCartModalTitle: string;
  reorderCartModalBody: string;
  reorderCartModalDismissButtonLabel: string;
  reorderCartModalConfirmButtonLabel: string;
  nameFieldPlaceholder: string;
  introCards: IntroCard[];
};

type Params = {
  pause?: boolean;
};
